import * as React from 'react';
import { useNavigate } from 'react-router';
import { KeyRound, Printer } from 'lucide-react';

import {
  normalizarChave,
  TAMANHO_MAXIMO_CHAVE,
  type EstadoImpressaoMarcacao,
} from '@/features/marcacao/formato';
import { Botao } from '@/components/ui/button';
import { Campo } from '@/components/ui/campo';
import { Entrada } from '@/components/ui/input';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

/**
 * Botão "Imprimir Marcação" da tela da solicitação.
 *
 * Antes de imprimir pergunta pela chave de confirmação: ela não existe no
 * índice do eSUS, e só quem tem a tela do eSUS aberta pode digitá-la. A chave
 * é opcional — em branco, a marcação sai sem ela.
 */
export function BotaoImprimirMarcacao({ solicitacaoId }: { solicitacaoId: string }) {
  const navigate = useNavigate();
  const [aberto, setAberto] = React.useState(false);
  const [comChave, setComChave] = React.useState(false);
  const [chave, setChave] = React.useState('');

  const abrirOuFechar = (valor: boolean) => {
    setAberto(valor);
    // Cada abertura começa da pergunta, sem a chave da marcação anterior.
    if (valor) {
      setComChave(false);
      setChave('');
    }
  };

  const imprimir = (chaveConfirmacao: string | null) => {
    setAberto(false);
    const estado: EstadoImpressaoMarcacao = { chaveConfirmacao };
    void navigate(`/solicitacoes/${solicitacaoId}/marcacao?formato=termica`, { state: estado });
  };

  return (
    <>
      <Botao variante="contorno" tamanho="sm" onClick={() => abrirOuFechar(true)}>
        <Printer aria-hidden="true" />
        Imprimir Marcação
      </Botao>

      <Dialogo open={aberto} onOpenChange={abrirOuFechar}>
        <DialogoConteudo>
          <DialogoCabecalho>
            <DialogoTitulo>Imprimir marcação</DialogoTitulo>
            <DialogoDescricao>
              A impressão vai com ou sem a chave de confirmação? A chave não vem do eSUS Regulação:
              para que ela saia no comprovante, copie-a da tela do eSUS.
            </DialogoDescricao>
          </DialogoCabecalho>

          {comChave ? (
            <form
              noValidate
              onSubmit={(evento) => {
                evento.preventDefault();
                imprimir(normalizarChave(chave));
              }}
            >
              <DialogoCorpo>
                <Campo
                  id="chave-confirmacao"
                  rotulo="Chave de Confirmação"
                  ajuda="Opcional. Em branco, a marcação sai sem a chave."
                >
                  <Entrada
                    value={chave}
                    onChange={(evento) => setChave(evento.target.value)}
                    maxLength={TAMANHO_MAXIMO_CHAVE}
                    autoComplete="off"
                    spellCheck={false}
                    autoFocus
                    className="font-mono uppercase"
                  />
                </Campo>
              </DialogoCorpo>

              <DialogoRodape>
                <Botao type="button" variante="contorno" onClick={() => setComChave(false)}>
                  Voltar
                </Botao>
                <Botao type="submit">
                  <Printer aria-hidden="true" />
                  Imprimir
                </Botao>
              </DialogoRodape>
            </form>
          ) : (
            <DialogoRodape>
              <Botao variante="contorno" onClick={() => imprimir(null)}>
                Sem Chave de Confirmação
              </Botao>
              <Botao onClick={() => setComChave(true)}>
                <KeyRound aria-hidden="true" />
                Com Chave de Confirmação
              </Botao>
            </DialogoRodape>
          )}
        </DialogoConteudo>
      </Dialogo>
    </>
  );
}
